import {
  Container,
  GlProgram,
  Geometry,
  Mesh,
  Shader,
  Texture,
  UniformGroup,
} from "pixi.js";

// Background "ripple" field. Each emitter spawns concentric horizontal
// ripples expanding outward from its X position. Ripples are full vertical
// height (with a soft top/bottom falloff), travel at constant speed, and
// dim as they recede.
//
// Visual recipe per emitter:
//   - At any time t, several ripples exist: r_n = ((t - n*period) * speed)
//     for n = 0..N. Negative or expanding-past-reach ripples are skipped.
//   - For a given pixel, distance dx = |pixel.x - emitter.x|.
//   - Cross-section of ripple n at this pixel: gaussian(dx - r_n, sigma).
//   - Brightness fades with r_n: exp(-r_n / reach).
//   - Total emitter contribution = sum_n(cross_n * fade_n) * gain * vertEnv(y).
//
// Released emitters keep emitting their existing rings (no new births) and
// `gain` decays exponentially over RELEASE_TAU seconds.

export const RippleParams = {
  // Master switch. Waves are suspended by default (they're visually noisy);
  // flip via the localhost debug panel's "ripples" toggle to preview them.
  enabled: false,
  speed: 700,           // px / s — ripple travel speed (slider 200..2000)
  spacing: 0.45,        // seconds between successive ripples at V=1
  thicknessPx: 4,       // gaussian sigma_x of each ring
  brightness: 1.0,      // global gain
  xOffset: 0,           // px nudge applied to every emitter's X (debug)
};

const MAX_EMITTERS = 16;
const MAX_RINGS_PER_EMITTER = 6;
const RELEASE_TAU = 0.9;

const VERT = `
in vec2 aPosition;
in vec2 aUV;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vUv;
out vec2 vWorldPos;

void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUv = aUV;
  vWorldPos = aPosition;
}
`;

const FRAG = `
precision highp float;
in vec2 vUv;
in vec2 vWorldPos;
out vec4 fragColor;

uniform float uSigmaX;    // gaussian thickness of each ring (pixels)
uniform float uReach;     // attenuation distance (pixels)
uniform float uBright;    // global brightness scalar
uniform int uCount;

// All sized arrays; one element per emitter slot.
// Positions are in world/screen pixel coordinates, matching line.restX.
uniform vec2  uEmitterPos[${MAX_EMITTERS}];
uniform vec3  uEmitterColor[${MAX_EMITTERS}];
uniform float uEmitterGain[${MAX_EMITTERS}];
uniform float uEmitterHeight[${MAX_EMITTERS}];        // vertical influence (px)
// Up to MAX_RINGS_PER_EMITTER active radii per emitter, packed as
// uRingRadii[emitter*MAX + n]. Inactive radii are negative.
uniform float uRingRadii[${MAX_EMITTERS * MAX_RINGS_PER_EMITTER}];

void main() {
  // vWorldPos is the interpolated local aPosition, which equals world pixels
  // for this full-screen quad (no parent transform on the WaveField container).
  // Using aPosition directly avoids any filter-padding offset that would corrupt
  // a UV * uResolution computation.
  vec2 px = vWorldPos;
  vec3 col = vec3(0.0);

  float sigmaX2 = uSigmaX * uSigmaX;
  float reach = max(uReach, 1.0);

  for (int i = 0; i < ${MAX_EMITTERS}; i++) {
    if (i >= uCount) break;
    float gain = uEmitterGain[i];
    if (gain <= 0.001) continue;

    vec2 epos = uEmitterPos[i];
    float dx = abs(px.x - epos.x);

    // Vertical envelope: gaussian band centred at emitter.y, full-height
    // when emitterHeight is large. Use heightSigma = emitterHeight * 0.5.
    float dy = px.y - epos.y;
    float hSigma = max(uEmitterHeight[i] * 0.5, 1.0);
    float vEnv = exp(-(dy * dy) / (hSigma * hSigma));

    float ringSum = 0.0;
    for (int n = 0; n < ${MAX_RINGS_PER_EMITTER}; n++) {
      float r = uRingRadii[i * ${MAX_RINGS_PER_EMITTER} + n];
      if (r < 0.0) continue;
      float d = dx - r;
      float band = exp(-(d * d) / sigmaX2);
      float dist = exp(-r / reach);
      ringSum += band * dist;
    }

    col += uEmitterColor[i] * gain * vEnv * ringSum * uBright;
  }

  fragColor = vec4(col, 1.0);
}
`;

interface Emitter {
  restX: number;
  v: number;
  held: boolean;
  gain: number;
  // Base brightness of the emitting line (0..1). Multiplies the per-emitter
  // gain so dim spectral lines emit dim ripples, bright lines emit bright
  // ripples — the wave's "loudness" is proportional to the line's voice.
  baseIntensity: number;
  color: { r: number; g: number; b: number };
  fingerY: number;
  // Accumulated age. Ring n's radius = (age - n * period) * speed.
  age: number;
  // Per-emitter `period` (seconds between successive rings) — frozen at the
  // V the emitter had when each ring was spawned, but for simplicity we
  // recompute live from current V (visible effect is small).
  // `nextRingAge` increments by `period` each time a new ring is born; this
  // is the age at which ring index `nextRingIdx % MAX_RINGS` will spawn.
  nextRingAge: number;
  // Active ring birth ages — ring i was born at birthAges[i]. Radius lookup
  // at time `age` is (age - birthAges[i]) * speed.
  birthAges: Float32Array;        // length MAX_RINGS_PER_EMITTER
  ringActive: boolean[];          // length MAX_RINGS_PER_EMITTER
  nextRingSlot: number;
  slot: number;
}

export class WaveField {
  private container: Container;
  private mesh: Mesh<Geometry>;
  private stageWidth: number;
  private stageHeight: number;
  private emitters = new Map<number, Emitter>();
  private freeSlots: number[] = [];

  private uPos: Float32Array;
  private uColor: Float32Array;
  private uGain: Float32Array;
  private uHeight: Float32Array;
  private uRingRadii: Float32Array;
  private uniforms: UniformGroup;

  constructor(parent: Container, stageWidth: number, stageHeight: number) {
    this.container = new Container();
    this.stageWidth = stageWidth;
    this.stageHeight = stageHeight;
    this.container.visible = RippleParams.enabled;
    parent.addChild(this.container);

    for (let i = MAX_EMITTERS - 1; i >= 0; i--) this.freeSlots.push(i);

    this.uPos = new Float32Array(MAX_EMITTERS * 2);
    this.uColor = new Float32Array(MAX_EMITTERS * 3);
    this.uGain = new Float32Array(MAX_EMITTERS);
    this.uHeight = new Float32Array(MAX_EMITTERS);
    this.uRingRadii = new Float32Array(MAX_EMITTERS * MAX_RINGS_PER_EMITTER);
    this.uRingRadii.fill(-1);

    this.uniforms = new UniformGroup({
      uSigmaX:     { value: RippleParams.thicknessPx, type: "f32" },
      uReach:      { value: stageWidth * 0.6,          type: "f32" },
      uBright:     { value: RippleParams.brightness,                     type: "f32" },
      uCount:      { value: 0,                                           type: "i32" },
      uEmitterPos:    { value: this.uPos,       type: "vec2<f32>", size: MAX_EMITTERS },
      uEmitterColor:  { value: this.uColor,     type: "vec3<f32>", size: MAX_EMITTERS },
      uEmitterGain:   { value: this.uGain,      type: "f32",       size: MAX_EMITTERS },
      uEmitterHeight: { value: this.uHeight,    type: "f32",       size: MAX_EMITTERS },
      uRingRadii:     { value: this.uRingRadii, type: "f32",       size: MAX_EMITTERS * MAX_RINGS_PER_EMITTER },
    });

    const positions = new Float32Array([
      0, 0,
      stageWidth, 0,
      stageWidth, stageHeight,
      0, stageHeight,
    ]);
    const uvs = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
    const indices = new Uint32Array([0, 1, 2, 0, 2, 3]);
    const geometry = new Geometry({
      attributes: {
        aPosition: { buffer: positions, format: "float32x2" },
        aUV:       { buffer: uvs,       format: "float32x2" },
      },
      indexBuffer: indices,
    });

    const glProgram = GlProgram.from({
      vertex: `#version 300 es\n${VERT}`,
      fragment: `#version 300 es\n${FRAG}`,
    });
    const shader = new Shader({
      glProgram,
      resources: { waveUniforms: this.uniforms },
    });
    (shader as unknown as { texture: Texture }).texture = Texture.WHITE;

    const mesh = new Mesh<Geometry>({
      geometry,
      shader: shader as unknown as Mesh["shader"],
    });
    mesh.blendMode = "add";
    this.container.addChild(mesh);
    this.mesh = mesh;
  }

  setStageSize(w: number, h: number) {
    this.stageWidth = w;
    this.stageHeight = h;
    const u = this.uniforms.uniforms as Record<string, unknown>;
    u.uReach = w * 0.6;
    const buf = this.mesh.geometry.getBuffer("aPosition");
    const positions = buf.data as Float32Array;
    positions[0] = 0;  positions[1] = 0;
    positions[2] = w;  positions[3] = 0;
    positions[4] = w;  positions[5] = h;
    positions[6] = 0;  positions[7] = h;
    buf.update();
  }

  // Suspend or resume the whole wave field. Hides the mesh (no GPU cost) and
  // drops any active emitters so a later resume starts clean.
  setEnabled(on: boolean) {
    RippleParams.enabled = on;
    this.container.visible = on;
    if (!on) {
      this.emitters.clear();
      this.uGain.fill(0);
      this.uRingRadii.fill(-1);
      this.freeSlots.length = 0;
      for (let i = MAX_EMITTERS - 1; i >= 0; i--) this.freeSlots.push(i);
    }
  }

  noteOn(lineIndex: number, restX: number, color: number, fingerY: number, baseIntensity: number) {
    if (!RippleParams.enabled) return;
    let em = this.emitters.get(lineIndex);
    if (!em) {
      const slot = this.freeSlots.pop();
      if (slot === undefined) return;
      em = {
        restX,
        v: 1 - fingerY / this.stageHeight,
        held: true,
        gain: 1,
        baseIntensity,
        color: unpackColor(color),
        fingerY,
        age: 0,
        nextRingAge: 0,
        birthAges: new Float32Array(MAX_RINGS_PER_EMITTER),
        ringActive: new Array(MAX_RINGS_PER_EMITTER).fill(false),
        nextRingSlot: 0,
        slot,
      };
      this.emitters.set(lineIndex, em);
    } else {
      em.held = true;
      em.gain = 1;
      em.v = 1 - fingerY / this.stageHeight;
      em.fingerY = fingerY;
      em.baseIntensity = baseIntensity;
    }
  }

  setV(lineIndex: number, fingerY: number) {
    const em = this.emitters.get(lineIndex);
    if (!em) return;
    em.fingerY = fingerY;
    em.v = 1 - fingerY / this.stageHeight;
  }

  noteOff(lineIndex: number) {
    const em = this.emitters.get(lineIndex);
    if (!em) return;
    em.held = false;
  }

  step(dtSec: number) {
    if (!RippleParams.enabled) return;
    const dt = Math.min(dtSec, 0.05);
    const reap: number[] = [];
    this.uGain.fill(0);

    const reach = this.stageWidth * 0.6;
    const farLimitPx = this.stageWidth; // ring is dead once past stage width

    for (const [idx, em] of this.emitters) {
      em.age += dt;
      const v01 = Math.max(0, Math.min(1, em.v));
      // Spawn rate and travel speed stay constant — V only modulates
      // brightness (set below in uGain).
      const period = RippleParams.spacing;
      const speed = RippleParams.speed;

      // Spawn new rings while held.
      if (em.held) {
        while (em.age >= em.nextRingAge) {
          em.birthAges[em.nextRingSlot] = em.nextRingAge;
          em.ringActive[em.nextRingSlot] = true;
          em.nextRingSlot = (em.nextRingSlot + 1) % MAX_RINGS_PER_EMITTER;
          em.nextRingAge += period;
        }
      }

      // Released: gain decays.
      if (!em.held) {
        em.gain *= Math.exp(-dt / RELEASE_TAU);
      }

      // Compute current radii; deactivate rings past their reach.
      const slot = em.slot;
      const base = slot * MAX_RINGS_PER_EMITTER;
      let anyActive = false;
      for (let n = 0; n < MAX_RINGS_PER_EMITTER; n++) {
        if (!em.ringActive[n]) {
          this.uRingRadii[base + n] = -1;
          continue;
        }
        const r = (em.age - em.birthAges[n]) * speed;
        if (r > farLimitPx) {
          em.ringActive[n] = false;
          this.uRingRadii[base + n] = -1;
          continue;
        }
        this.uRingRadii[base + n] = r;
        anyActive = true;
      }

      const reapThis = !em.held && (em.gain < 0.01 || !anyActive);
      if (reapThis) {
        reap.push(idx);
        continue;
      }

      // Ripples always originate from the line's vertical centre.
      // Vertical influence is wide so rings read as full-height background bars.
      const heightPx = this.stageHeight * 0.7;

      this.uPos[slot * 2 + 0] = em.restX + RippleParams.xOffset;
      this.uPos[slot * 2 + 1] = this.stageHeight / 2;
      this.uColor[slot * 3 + 0] = em.color.r;
      this.uColor[slot * 3 + 1] = em.color.g;
      this.uColor[slot * 3 + 2] = em.color.b;
      // V (finger Y, top=1) is the primary intensity driver — like audio
      // pressure. RippleParams.brightness slider stacks on top in the shader.
      this.uGain[slot] = em.gain * (0.05 + 1.05 * v01) * em.baseIntensity;
      this.uHeight[slot] = heightPx;
    }
    for (const idx of reap) {
      const em = this.emitters.get(idx)!;
      const base = em.slot * MAX_RINGS_PER_EMITTER;
      for (let n = 0; n < MAX_RINGS_PER_EMITTER; n++) this.uRingRadii[base + n] = -1;
      this.freeSlots.push(em.slot);
      this.emitters.delete(idx);
    }

    // uCount = highest active slot + 1.
    let highest = -1;
    for (let i = 0; i < MAX_EMITTERS; i++) if (this.uGain[i] > 0) highest = i;
    const u = this.uniforms.uniforms as Record<string, unknown>;
    u.uCount = highest + 1;
    u.uSigmaX = RippleParams.thicknessPx;
    u.uBright = RippleParams.brightness;
    u.uReach = reach;
  }
}

function unpackColor(rgb: number): { r: number; g: number; b: number } {
  return {
    r: ((rgb >> 16) & 0xff) / 255,
    g: ((rgb >> 8) & 0xff) / 255,
    b: (rgb & 0xff) / 255,
  };
}
